import React, { useState, useEffect } from "react";
import { 
  Calendar, 
  User, 
  Clock, 
  Video, 
  MapPin, 
  ChevronRight, 
  Search,
  Filter,
  CheckCircle2,
  XCircle,
  MoreVertical,
  Activity
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { db } from "../../firebase/config";
import { ref, onValue, update } from "firebase/database";
import DoctorSidebar from "./DoctorSidebar";
import "./DoctorAppointments.css";

const DoctorAppointments = () => {
  const { currentUser } = useAuth();
  const [appointments, setAppointments] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("All");

  useEffect(() => {
    if (!currentUser) return;

    // 1. Synchronize Physician-Specific Session Registry
    const appointmentsRef = ref(db, "appointments");
    const unsubscribe = onValue(appointmentsRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const mySessions = Object.entries(data)
          .map(([id, val]) => ({
            id,
            ...val
          }))
          .filter(appt => appt.doctorId === currentUser.uid);
        
        setAppointments(mySessions.sort((a, b) => new Date(a.date) - new Date(b.date)));
      } else {
        setAppointments([]);
      }
    });

    return () => unsubscribe();
  }, [currentUser]);

  const updateStatus = async (apptId, newStatus) => {
    try {
      await update(ref(db, `appointments/${apptId}`), { status: newStatus });
    } catch (error) {
      console.error("STATUS_UPDATE_FAILURE:", error);
    }
  };

  const filteredAppointments = appointments.filter(appt => {
    const matchesSearch = appt.patientName.toLowerCase().includes(searchTerm.toLowerCase()) || 
                          appt.patientCustomId.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterStatus === "All" || appt.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  return (
    <div className="doctor-appt-shell">
      <DoctorSidebar />
      
      <main className="doctor-appt-content">
        {/* Sovereign HUD Visual Overlays */}
        <div className="doctor-appt-grid-overlay"></div>
        <div className="doctor-appt-scanline"></div>

        <header className="doctor-appt-header">
          <div className="header-meta">
            <div className="doc-appt-hud-badge">
              <Activity size={14} className="icon-pulse" />
              <span>PHYSICIAN_SESSION_REGISTRY_V1.0</span>
            </div>
            <h1>Session <span className="text-gradient">Registry</span></h1>
            <p>Monitor and manage verified clinical appointments across your patient network.</p>
          </div>
        </header>

        <section className="doctor-appt-controls">
          <div className="search-registry glass-card">
            <Search size={18} className="icon-dim" />
            <input 
              type="text" 
              placeholder="Filter by Patient Name or ID..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          
          <div className="filter-registry glass-card">
            <Filter size={18} className="icon-dim" />
            <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
              <option value="All">ALL_STATUS</option>
              <option value="Confirmed">CONFIRMED</option>
              <option value="Completed">COMPLETED</option>
              <option value="Cancelled">CANCELLED</option>
            </select>
          </div>
        </section>

        <div className="doctor-appt-registry-list">
          {filteredAppointments.length > 0 ? (
            filteredAppointments.map((appt) => (
              <div key={appt.id} className="doctor-appt-card glass-card">
                <div className="patient-node">
                  <div className="patient-avatar">
                    {appt.patientName.charAt(0)}
                  </div>
                  <div className="patient-info">
                    <h3>{appt.patientName}</h3>
                    <span className="patient-id">{appt.patientCustomId}</span>
                  </div>
                </div>

                <div className="session-temporal-data">
                  <div className="temporal-item">
                    <Calendar size={16} />
                    <span>{new Date(appt.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                  </div>
                  <div className="temporal-item">
                    <Clock size={16} />
                    <span>{appt.time}</span>
                  </div>
                </div>

                <div className="session-modality">
                  <div className="modality-badge">
                    {appt.type === "Video Consultation" ? <Video size={16} /> : <MapPin size={16} />}
                    <span>{appt.type}</span>
                  </div>
                  <span className="specialty-label">{appt.specialty}</span>
                </div>

                <div className="session-status-hub">
                  <span className={`status-pill ${appt.status.toLowerCase()}`}>{appt.status}</span>
                  <div className="action-relay">
                    {appt.status === "Confirmed" && (
                      <button 
                        className="btn-complete" 
                        title="Mark as Completed"
                        onClick={() => updateStatus(appt.id, "Completed")}
                      >
                        <CheckCircle2 size={18} />
                      </button>
                    )}
                    <button className="btn-more">
                      <MoreVertical size={18} />
                    </button>
                  </div>
                </div>
                
                <div className="card-brackets"></div>
              </div>
            ))
          ) : (
            <div className="empty-registry-state glass-card">
              <Activity size={48} className="icon-dim icon-pulse" />
              <h3>NO_IDENTIFIED_SESSIONS</h3>
              <p>The clinical registry is currently empty for your physician node.</p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default DoctorAppointments;
